import { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { User } from "lucide-react";
import { useTranslation } from "react-i18next";

interface AvatarViewerProps {
  open: boolean;
  onClose: () => void;
  avatarUrl: string;
  username?: string | null;
}

export const AvatarViewer = ({ open, onClose, avatarUrl, username }: AvatarViewerProps) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);

  if (!open) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getInitials = () => {
    if (!username) return 'U';
    return username.charAt(0).toUpperCase();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center animate-in fade-in duration-200"
      onClick={handleBackdropClick}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors text-white"
        aria-label={t('common.close')}
      >
        <X className="w-6 h-6" />
      </button>

      {/* Avatar image container */}
      <div className="relative max-w-[90vw] max-h-[90vh] animate-in zoom-in-95 duration-200">
        {!imageLoaded && (
          <div className="w-64 h-64 md:w-80 md:h-80 rounded-full bg-muted animate-pulse flex items-center justify-center">
            <User className="w-24 h-24 text-muted-foreground" />
          </div>
        )}
        <img
          src={avatarUrl}
          alt={username || 'User avatar'}
          className={`w-64 h-64 md:w-80 md:h-80 rounded-full object-cover ${imageLoaded ? 'block' : 'hidden'}`}
          onLoad={() => setImageLoaded(true)}
        />
      </div>
    </div>,
    document.body
  );
};
